"use client";
import {
  Steps as AntSteps,
  Button,
  Divider,
  StepsProps,
} from "@basic-components";
import { ReactNode, useState } from "react";
import styled from "styled-components";
import { ArrowLeftOutlined } from "@ant-design/icons";
import React from "react";

export const StepContext = React.createContext({
  current: 0,
  onNext: () => {},
  onPrev: () => {},
});

export const useStep = () => {
  return React.useContext(StepContext);
};

const StyledSteps = styled(AntSteps)`
  // 隐藏底部激活线（::after 和 ::before）
  .ant-steps-item {
    &::before {
      display: none !important;
    }

    // 激活项文字颜色
    &.ant-steps-item-active {
      .ant-steps-item-title {
        color: var(--ant-color-primary) !important;
      }
    }

    .ant-steps-item-wrapper {
      border-radius: 9999px;
      padding: 0px 16px;
      border: 1px solid var(--ant-color-border);
      padding-block: 4px !important;
    }
    &.ant-steps-item-active {
      .ant-steps-item-wrapper {
        border-color: var(--ant-color-primary);
        background-color: var(--ant-color-primary-6);
      }
    }
  }
`;

export const ActionsLayout = ({
  left,
  children,
}: {
  left?: ReactNode;
  children: ReactNode;
}) => {
  const { onPrev, current } = useStep();
  const _left =
    !!current &&
    (left || (
      <Button color="primary" variant="outlined" icon={<ArrowLeftOutlined />} onClick={onPrev}>
        上一步
      </Button>
    ));

  return (
    <div className="relative w-full">
    <div className="absolute bottom-0 left-0 bg-white rounded-md w-full">
      <Divider className="!mb-0" />
      <div className="flex justify-center p-4">
        <div className="w-full max-w-4xl">
          <div className={!_left ? "flex justify-end" : "flex justify-between"}>
            {_left}
            <div className="flex space-x-2">{children}</div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

const StepControl = ({
  onNext,
  onPrev,
  children,
  current,
}: {
  onNext: () => void;
  onPrev: () => void;
  children: ReactNode;
  current: number;
}) => {

  return (
    <StepContext.Provider value={{ current, onNext, onPrev }}>
      <div className="relative min-h-[400px]">
        {React.Children.map(
          children,
          (child: React.ReactNode, index: number) =>
            React.isValidElement(child) && (
              <div
                key={index}
                style={{
                  display: index === current ? 'block' : 'none',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                }}
              >
                {React.cloneElement(
                  child as React.ReactElement<{
                    onNext: () => void;
                    onPrev: () => void;
                  }>,
                  {
                    onNext: onNext,
                    onPrev: onPrev,
                  }
                )}
              </div>
            )
        )}
      </div>
    </StepContext.Provider>
  );
};

export function Steps({
  items = [],
  children,
}: {
  items?: StepsProps["items"];
  children: ReactNode;
}) {
  const [activeStep, setActiveStep] = useState(0);
  const handleNext = () => {
    if (activeStep === items.length - 1) {
      return;
    }
    setActiveStep(activeStep + 1);
  };
  const handlePrev = () => {
    if (activeStep === 0) {
      return;
    }
    setActiveStep(activeStep - 1);
  };
  return (
    <>
        <div className="flex justify-center w-full">
        <div className="w-full max-w-4xl">
          <StyledSteps
            current={activeStep}
            items={items}
            size="small"
            type="navigation"
            styles={{
              root: {
                border: "none",
              },
            }}
          />
        </div>
      </div>
      <Divider className="!mb-0" />
      <StepControl current={activeStep} onNext={handleNext} onPrev={handlePrev}>
        {children}
      </StepControl>
    </>
  );
}
